import { useState } from 'react';
import Pill from './Pill.jsx';
import InfoIcon from './InfoIcon.jsx';
import './ClubCard.css';

const CATEGORY_LABELS = {
  academic: 'Academic',
  arts: 'Arts',
  service: 'Service',
  stem: 'STEM',
  spirit: 'Spirit',
  'special-interest': 'Special Interest',
};

const GRADE_SUFFIX = { 6: '6th', 7: '7th', 8: '8th' };

function HeartIcon({ filled }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );
}

export default function ClubCard({ club, isFavorited, onToggleFavorite, showFeaturedPill }) {
  const [animating, setAnimating] = useState(false);

  const {
    name,
    shortName,
    category,
    grades,
    competitive,
    highlight,
    capacity,
    meetingDay,
    meetingTime,
    meetingLocation,
    description,
    advisor,
    advisorEmail,
    requirements,
    fieldTrips,
    cost,
    notes,
    source,
    verificationNeeded,
    verificationNote,
  } = club;

  const hasDetails = requirements || fieldTrips || cost || notes;

  const effectiveSource =
    verificationNeeded && verificationNote
      ? { ...source, note: verificationNote, verificationNeeded: true }
      : source;

  const dayText     = meetingDay     || 'Day TBA';
  const timeText    = meetingTime    || 'Time TBA';
  const locationText = meetingLocation || 'Location TBA';

  function handleHeartClick() {
    setAnimating(true);
    onToggleFavorite(club.id);
    setTimeout(() => setAnimating(false), 400);
  }

  return (
    <div className={`club-card${verificationNeeded ? ' club-card--caution' : ''}`}>

      {/* ── Top row: name + heart + InfoIcon ── */}
      <div className="club-card-top">
        <h3 className="club-card-name">
          {name}
          {shortName && shortName !== name && (
            <span className="club-card-short-name"> ({shortName})</span>
          )}
        </h3>
        <div className="club-card-top-actions">
          <InfoIcon source={effectiveSource} />
          <button
            type="button"
            className={[
              'club-heart-btn',
              isFavorited ? 'club-heart-btn--favorited' : '',
              animating ? 'club-heart-btn--animating' : '',
            ].filter(Boolean).join(' ')}
            aria-label={isFavorited ? `Remove ${name} from favorites` : `Add ${name} to favorites`}
            aria-pressed={isFavorited}
            onClick={handleHeartClick}
          >
            <HeartIcon filled={isFavorited} />
          </button>
        </div>
      </div>

      {/* ── Pill row ── */}
      <div className="club-card-pills">
        <Pill variant="default" size="sm">{CATEGORY_LABELS[category] ?? category}</Pill>
        {grades.map(g => (
          <Pill key={g} variant="primary" size="sm">{GRADE_SUFFIX[g] ?? `${g}th`}</Pill>
        ))}
        {competitive && <Pill variant="accent" size="sm">Competitive</Pill>}
        {capacity && <Pill variant="caution" size="sm">{capacity} spots</Pill>}
        {showFeaturedPill && highlight && <Pill variant="required" size="sm">Featured</Pill>}
      </div>

      {/* ── Meeting info ── */}
      <p className="club-card-meeting">
        <span className="club-card-meeting-day">{dayText}</span>
        <span className="club-card-meeting-sep" aria-hidden="true"> · </span>
        <span className="club-card-meeting-time">{timeText}</span>
        <span className="club-card-meeting-sep" aria-hidden="true"> · </span>
        <span className="club-card-meeting-loc">{locationText}</span>
      </p>

      {/* ── Description ── */}
      <p className="club-card-description">{description}</p>

      {/* ── Advisor ── */}
      {advisor && advisor !== 'TBA' && (
        <p className="club-card-advisor">
          Advisor:{' '}
          {advisorEmail ? (
            <a href={`mailto:${advisorEmail}`} className="club-card-advisor-link">
              {advisor}
            </a>
          ) : (
            advisor
          )}
        </p>
      )}

      {/* ── Expandable details ── */}
      {hasDetails && (
        <details className="club-card-details">
          <summary className="club-card-details-summary">More info</summary>
          <div className="club-card-details-body">
            {requirements && (
              <div className="club-detail-row">
                <span className="club-detail-label">Requirements</span>
                <p className="club-detail-text">{requirements}</p>
              </div>
            )}
            {fieldTrips && (
              <div className="club-detail-row">
                <span className="club-detail-label">Events & competitions</span>
                <p className="club-detail-text">{fieldTrips}</p>
              </div>
            )}
            {cost && (
              <div className="club-detail-row">
                <span className="club-detail-label">Cost</span>
                <p className="club-detail-text">{cost}</p>
              </div>
            )}
            {notes && (
              <div className="club-detail-row">
                <span className="club-detail-label">Notes</span>
                <p className="club-detail-text">{notes}</p>
              </div>
            )}
          </div>
        </details>
      )}
    </div>
  );
}
